import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { ArticleSchema, BreadcrumbListSchema, WebPageSchema } from '@/components/feature/StructuredData';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-xl text-foreground-600 mb-4">文章不存在</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  const otherArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug !== article.categorySlug)
    .slice(0, 3);

  return (
    <>
      <ArticleSchema
        title={article.title}
        description={article.excerpt}
        url={`https://www.91zaixianshipin.com/article/${article.slug}`}
        imageUrl={article.image}
        datePublished={article.date}
        dateModified={article.date}
        authorName={article.author}
      />
      <WebPageSchema
        title={`${article.title} - 91在线视频`}
        description={article.excerpt}
        url={`https://www.91zaixianshipin.com/article/${article.slug}`}
        datePublished={article.date}
        dateModified={article.date}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'https://www.91zaixianshipin.com/' },
          { name: article.category, url: `https://www.91zaixianshipin.com/category/${article.categorySlug}` },
          { name: article.title, url: `https://www.91zaixianshipin.com/article/${article.slug}` },
        ]}
      />
      <Navbar />
      <main className="pt-16">
        <nav className="w-full px-4 md:px-6 py-4" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto">
            <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
              </li>
              <li>
                <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-500 transition-colors">
                  {article.category}
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
              </li>
              <li>
                <span className="text-foreground-800 font-medium line-clamp-1">{article.title.slice(0, 20)}...</span>
              </li>
            </ol>
          </div>
        </nav>

        <article>
          <header className="bg-background-50 py-10 md:py-16">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label whitespace-nowrap">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-500">{article.date}</span>
                  <span className="text-xs text-foreground-500">·</span>
                  <span className="text-xs text-foreground-500">{article.author}</span>
                </div>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>
                <p className="text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>
                <div className="flex items-center gap-4 mt-6">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-eye-line text-foreground-400"></i>
                    <span className="text-sm text-foreground-500">
                      {article.readCount > 100000
                        ? `${(article.readCount / 10000).toFixed(1)}万`
                        : article.readCount}{' '}
                      阅读
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="text-sm text-foreground-500">{article.rating} 分</span>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <section className="relative w-full h-[350px] md:h-[500px] overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40"></div>
          </section>

          <div className="flex flex-col lg:flex-row gap-10 py-12 md:py-16">
            <div className="flex-1 w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <div className="prose prose-lg max-w-none">
                  <p className="text-foreground-800 leading-relaxed mb-6">
                    {article.excerpt}
                  </p>
                  <p className="text-foreground-800 leading-relaxed mb-6">
                    据知情人士透露，{article.title.slice(0, 8)}的相关事件在圈内早已不是秘密。多位业内人士向本平台证实了这一消息的真实性，并表示更多细节将在后续逐步披露。
                  </p>
                  <p className="text-foreground-800 leading-relaxed mb-6">
                    事实上，这一事件在社交媒体上已经引发了广泛讨论。微博话题阅读量在短短几小时内突破千万，大量网友纷纷发表自己的看法。有网友表示："这次真的是大瓜，坐等后续发展。"也有业内人士分析认为，此事可能只是冰山一角，后面还有更多猛料等待揭晓。
                  </p>
                  <h2 className="text-xl font-heading font-bold text-foreground-950 mt-10 mb-4">事件回顾</h2>
                  <p className="text-foreground-800 leading-relaxed mb-6">
                    回顾整个事件的来龙去脉，最早可以追溯到本月月初。当时就有细心的网友发现了一些蛛丝马迹，但由于缺乏直接证据，并未引起太大关注。直到最近几天，随着更多关键信息的浮出水面，这一事件才彻底引爆了舆论场。
                  </p>
                  <p className="text-foreground-800 leading-relaxed mb-6">
                    多位娱乐评论人就此发表了专业分析。知名娱评人老张表示："从目前的证据链来看，这件事的可信度相当高。不过我们还是要等待当事人的正式回应，不能过早下结论。"而另一位业内人士则透露，相关方面已经在积极沟通，预计近期会有官方声明发布。
                  </p>
                  <h2 className="text-xl font-heading font-bold text-foreground-950 mt-10 mb-4">网友反应</h2>
                  <p className="text-foreground-800 leading-relaxed mb-6">
                    截至目前，各大社交平台上的讨论热度持续攀升。微博、抖音、小红书等平台的相关话题均占据热搜前列。网友们的态度呈现明显的两极分化——有人选择相信并力挺，也有人持怀疑态度等待更多证据。
                  </p>
                  <p className="text-foreground-800 leading-relaxed mb-6">
                    值得注意的是，此次事件中，理性吃瓜的声音明显增多。越来越多的网友呼吁："在真相大白之前，不要轻易站队，理性看待每一个瓜。"这种态度转变，也反映出当下网络舆论环境的逐渐成熟。
                  </p>
                  <div className="bg-background-100 rounded-lg p-6 my-10 border-l-4 border-primary-500">
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      <strong className="text-foreground-950">免责声明：</strong>
                      本文内容来源于网络爆料与网友投稿，本平台仅作整理发布，不对内容真实性作任何保证。如有不实内容，请联系我们进行核实处理。请读者理性看待，勿传播未经证实的信息。
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200">
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category/${article.categorySlug}`}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>

                <div className="flex items-center justify-between mt-10 pt-6 border-t border-background-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary-200 flex items-center justify-center">
                      <i className="ri-user-line text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-label font-semibold text-foreground-950">{article.author}</p>
                      <p className="text-xs text-foreground-500">娱乐资讯编辑</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors cursor-pointer" aria-label="分享到微博">
                      <i className="ri-weibo-line text-foreground-600"></i>
                    </button>
                    <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors cursor-pointer" aria-label="分享到微信">
                      <i className="ri-wechat-line text-foreground-600"></i>
                    </button>
                    <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors cursor-pointer" aria-label="复制链接">
                      <i className="ri-link text-foreground-600"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <aside className="lg:w-80 w-full px-4 md:px-6 lg:pr-6 lg:pl-0">
              <div className="sticky top-24">
                {relatedArticles.length > 0 && (
                  <div className="mb-10">
                    <h3 className="text-sm font-label font-semibold text-foreground-950 mb-4 uppercase tracking-wider">
                      同分类推荐
                    </h3>
                    <div className="flex flex-col gap-4">
                      {relatedArticles.map((ra) => (
                        <Link
                          key={ra.id}
                          to={`/article/${ra.slug}`}
                          className="flex gap-3 group cursor-pointer"
                        >
                          <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
                            <img
                              src={ra.image}
                              alt={ra.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-heading font-bold text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                              {ra.title}
                            </h4>
                            <span className="text-xs text-foreground-500 mt-1.5 block">{ra.date}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {otherArticles.length > 0 && (
                  <div className="mb-10">
                    <h3 className="text-sm font-label font-semibold text-foreground-950 mb-4 uppercase tracking-wider">
                      看了又看
                    </h3>
                    <div className="flex flex-col gap-4">
                      {otherArticles.map((oa) => (
                        <Link
                          key={oa.id}
                          to={`/article/${oa.slug}`}
                          className="flex gap-3 group cursor-pointer"
                        >
                          <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
                            <img
                              src={oa.image}
                              alt={oa.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs text-primary-500 font-label">{oa.category}</span>
                            <h4 className="text-sm font-heading font-bold text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors mt-0.5">
                              {oa.title}
                            </h4>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-foreground-950 rounded-lg p-5">
                  <h3 className="text-sm font-label font-semibold text-background-50 mb-3">
                    下载91在线视频APP
                  </h3>
                  <p className="text-xs text-background-50/60 mb-4 leading-relaxed">
                    第一时间获取全网最热吃瓜爆料，不落下任何一个热门事件
                  </p>
                  <a
                    href="#"
                    className="block w-full text-center px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-label font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    立即下载
                  </a>
                </div>
              </div>
            </aside>
          </div>

          <section className="bg-background-100 py-12 md:py-16">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                  网友神评
                </h2>
                <div className="space-y-6">
                  {article.comments && article.comments.map((comment, idx) => (
                    <div key={idx} className="flex gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                        <img
                          src={comment.avatar}
                          alt={comment.user}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-label font-semibold text-foreground-950">{comment.user}</span>
                          <div className="flex items-center gap-0.5">
                            {Array.from({ length: comment.rating }).map((_, i) => (
                              <i key={i} className="ri-star-fill text-xs text-accent-500"></i>
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-foreground-700 leading-relaxed">{comment.content}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <button className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer flex items-center gap-1">
                            <i className="ri-thumb-up-line"></i> {(Math.random() * 500 + 10).toFixed(0)}
                          </button>
                          <button className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer flex items-center gap-1">
                            <i className="ri-chat-1-line"></i> 回复
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}